import { MutationTuple } from "@apollo/client";
import { ResultOf } from "@graphql-typed-document-node/core";
import { toast } from "sonner";

import { Badge, Box, Button, HStack, List, ListItem, Text } from "@chakra-ui/react";

import { FragmentType, getFragmentData, graphql } from "@/__generated__";
import {
  MUserProfilePage_UpdateProfileMutation,
  MUserProfilePage_UpdateProfileMutationVariables,
} from "@/__generated__/graphql";
import { assertHasNoUserErrors } from "@/utils/legacyErrorHandler";

const UserProfileEmails_User = graphql(`
  fragment UserProfileEmails_User on User {
    id
    primaryEmailAddress {
      databaseId
    }
    emailAddresses {
      nodes {
        ...UserProfileEmails_EmailAddress
      }
    }
  }
`);

const UserProfileEmails_EmailAddress = graphql(`
  fragment UserProfileEmails_EmailAddress on EmailAddress {
    id
    email
    databaseId
  }
`);

interface Props {
  user: FragmentType<typeof UserProfileEmails_User>;
  updateProfileMutation: MutationTuple<
    MUserProfilePage_UpdateProfileMutation,
    MUserProfilePage_UpdateProfileMutationVariables
  >;
}

export default function Emails({ updateProfileMutation, ...props }: Props) {
  const user = getFragmentData(UserProfileEmails_User, props.user);
  const primaryEmail = user.primaryEmailAddress;
  const emailAddresses = getFragmentData(UserProfileEmails_EmailAddress, user.emailAddresses?.nodes) ?? [];

  const [updateProfile, { loading }] = updateProfileMutation;

  const handleMakePrimary = (email: ResultOf<typeof UserProfileEmails_EmailAddress>) => async () => {
    try {
      const { data } = await updateProfile({
        variables: {
          input: {
            primaryEmailId: email.databaseId,
          },
        },
      });

      assertHasNoUserErrors(data?.usersUpdateProfile?.userErrors);

      toast.success("Primary email updated");
    } catch (error) {
      toast.error("Error updating primary email");
    }
  };

  return (
    <Box>
      <Text mb={2}>Emails</Text>
      <List variant="spaced">
        {emailAddresses.map(email => (
          <ListItem key={email.id}>
            <HStack justifyContent="space-between">
              <Text>{email.email}</Text>
              {email.databaseId == primaryEmail?.databaseId ? (
                <Badge my="1px">Primary</Badge>
              ) : (
                <Button isDisabled={loading} size="sm" variant="ghost" onClick={handleMakePrimary(email)}>
                  Make primary
                </Button>
              )}
            </HStack>
          </ListItem>
        ))}
      </List>
    </Box>
  );
}
